import { useMemo } from 'react';
import { Box, Link, List, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { dataConstants } from "../../../constants";
import { getRandomStr, roundNum } from "../../../helpers";
import { getPercent } from '../../../utils';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import useSWR from 'swr';
import _getLatestPrice from '../../../../pages/api/_getLatestPrice';
import * as send from '../../../../lib/analytics/send';

const { TOPMETRICS_TICKETS, TOPMETRICS_TICKETSANDNAMES } = dataConstants;

const Item = styled(Link)(({ theme }) => ({
  padding: theme.spacing(.5, .6),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: 'white',
  borderRadius: '7px',
  border: '1px solid #dadce0',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
}));

const Marquee = styled(Box)(() => ({
  padding: '7px 0 0 0',
  '& .stock-ticker': {
    overflow: 'hidden',
    userSelect: 'none',
    '--gap': '7px',
    display: 'flex',
    textTransform: 'uppercase',
    gap: 'var(--gap)',
    '& ul': {
      listStyle: 'none',
      flexShrink: 0,
      minWidth: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 'var(--gap)',
      animation: 'scroll 57s linear infinite',
    },
    '&:hover ul': {
      animationPlayState: 'paused',
    },
    '@keyframes scroll': {
      to: {
        transform: 'translateX(calc(-100% - var(--gap)))',
      },
    },
    '& .company, & .price': {
      fontWeight: 'bold',
    },
    '& .price': {
      marginInline: '4px',
    },
    '& .plus .change, & .plus::before': {
      color: '#089981',
    },
    '& .minus .change, & .minus::before': {
      color: '#f23645',
    },
    '& .plus .MuiSvgIcon-root': {
      color: '#089981',
    },
    '& .minus .MuiSvgIcon-root': {
      color: '#f23645',
    },
  },
}));

const TopMetricsWithMarquee = ({ marketStatus }) => {
  const { data, error, isValidating } = useSWR(
    ["api/_getLatestPrice", TOPMETRICS_TICKETS],
    _getLatestPrice,
    {
      refreshInterval: 180000,
      keepPreviousData: true,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (error.status === 404) return;
        if (retryCount >= 10) return;
        setTimeout(() => revalidate({ retryCount }), 5000);
      }
    }
  );

  const tickers = useMemo(() => {
    if (!data?.getLatestPrice) return {};
    return data.getLatestPrice.reduce((acc, ticket) => {
      if (!acc[ticket.ticker]) acc[ticket.ticker] = [];
      acc[ticket.ticker].push(ticket);
      return acc;
    }, {});
  }, [data?.getLatestPrice]);

  if (error) return null;

  const renderTickerItems = () => TOPMETRICS_TICKETSANDNAMES.map(d => {
    const t = tickers[d.t];
    if (!t || t.length === 0) return null;

    const currentPrice = t[0]?.p;
    const previousPrice = t[1]?.p;
    const isPositive = currentPrice >= previousPrice;
    const hasFullData = currentPrice !== undefined && previousPrice !== undefined;
    const priceDiff = currentPrice - previousPrice;

    return (
      <li key={getRandomStr(7)} className={isPositive ? 'plus' : 'minus'}>
        <Item
          className="home-top-met"
          component={d?.u ? 'a' : 'div'}
          sx={{ cursor: d?.u ? 'pointer' : 'default' }}
          underline="none"
          href={d?.u || undefined}
          onClick={(e) => {
            if (d?.u) {
              send.uevent(e, `stock-home-top-met ${d?.t}`);
            } else {
              e.preventDefault();
            }
          }}
        >
          <Box component="span"
            sx={{
              boxAlign: 'center', alignItems: 'center', height: '36px', mt: .1, width: '36px', minWidth: '32px', borderRadius: '6px',
              marginRight: '8px', display: 'flex', bgcolor: isPositive ? '#e6f4ea' : '#fce8e6',
              color: isPositive ? '#137333' : '#AB1C39',
              '& svg': { height: '18px', margin: 'auto', fill: 'currentColor', flexShrink: 0 }
            }}
          >
            {isPositive ? <ArrowUpwardRoundedIcon /> : <ArrowDownwardRoundedIcon />}
          </Box>
          <Box component="div">
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={{ md: 2, sm: 2, xs: 1 }} sx={{ '& a': { fontSize: '.8rem', fontWeight: 600, color: '#202124', whiteSpace: 'nowrap' } }} >
              <Typography component="p" className="h-tick-name" sx={{ fontSize: '.77rem', fontWeight: 600, color: '#202124', whiteSpace: 'nowrap' }}>{d.n}</Typography>
              {hasFullData && (
                <Typography component="p" sx={{ fontSize: '.8rem', color: isValidating ? 'rgb(154,160,166)' : (isPositive ? 'rgb(14, 158, 25)' : '#AB1C39'), fontWeight: 600 }}>
                  {getPercent(currentPrice, previousPrice)}%
                </Typography>
              )}
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography component="p" className="h-tick-p" sx={{ fontSize: '.78rem', color: isValidating ? 'rgb(154,160,166)' : '#3c4043' }}>
                ${roundNum(currentPrice)}
              </Typography>
              {hasFullData && (
                <Typography component="p" sx={{ fontSize: '.78rem', textAlign: 'left', color: isValidating ? 'rgb(154,160,166)' : (isPositive ? 'rgb(14, 158, 25)' : '#AB1C39'), fontWeight: 400 }}>
                  {priceDiff >= 0 ? `+${roundNum(priceDiff)}` : roundNum(priceDiff)}
                </Typography>
              )}
            </Stack>
          </Box>
        </Item>
      </li>
    );
  });

  return (
    <Marquee>
      <div className="stock-ticker">
        <List sx={{ '& ul': { padding: 0 } }}>
          {renderTickerItems()}
        </List>
        <List sx={{ '& ul': { padding: 0 } }} aria-hidden="true">
          {renderTickerItems()}
        </List>
      </div>
      {marketStatus}
    </Marquee>
  );
};

export default TopMetricsWithMarquee;


// previously edited files for reference:

// import { useEffect, useState } from 'react';
// import {
//   Box, Link, List, Stack, Tab, Tabs, Typography, tabsClasses,
//   // Divider,
// } from '@mui/material';
// // import Link from 'next/link';
// import { styled } from '@mui/material/styles';
// import { dataConstants } from "../../../constants";
// import { getRandomStr, roundNum } from "../../../helpers";
// import { getPercent } from '../../../utils';
// import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
// import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
// import { useRouter } from 'next/router';
// // import getTickersInfo from '../../../../lib/stock/_getTickersInfo';
// import useSWR from 'swr';
// // import _getTickersInfo from '../../../../pages/api/_getTickersInfo';
// import _getLatestPrice from '../../../../pages/api/_getLatestPrice';
// import * as send from '../../../../lib/analytics/send';

// const { TOPMETRICS_TICKETS, TOPMETRICS_TICKETSANDNAMES } = dataConstants

// const convertData = (data) => {
//   if (data && data?.length > 0) {
//     const result = data?.reduce((acc, { ticker, r, p }) => {
//       if (!acc[ticker]) {
//         acc[ticker] = {
//           ticker,
//           price_data: [],
//         };
//       }

//       acc[ticker]?.price_data.push({ price: p });

//       return acc;
//     }, {});

//     return result;
//   }
// }

// const Item = styled(Link)(({ theme }) => ({
//   padding: theme.spacing(.5, .6),
//   textAlign: 'center',
//   color: theme.palette.text.secondary,
//   backgroundColor: 'white',
//   borderRadius: '7px',
//   border: '1px solid #dadce0',
//   whiteSpace: 'nowrap',
//   display: 'flex',
//   alignItmes: 'center',
//   textDecoration: 'none',
// }));

// const Marquee = styled(Box)(({ theme }) => ({
//   padding: '7px 0 0 0',
//   '& .stock-ticker': {
//     overflow: 'hidden',
//     userSelect: 'none',
//     '--gap': '7px',
//     display: 'flex',
//     textTransform: 'uppercase',
//     gap: 'var(--gap)',
//     '& ul': {
//       listStyle: 'none',
//       flexShrink: 0,
//       minWidth: '100%',
//       display: 'flex',
//       justifyContent: 'space-between',
//       alignItems: 'center',
//       gap: 'var(--gap)',
//       animation: 'scroll 57s linear infinite',
//     },
//     '&:hover ul': {
//       animationPlayState: 'paused',
//     },
//     '@keyframes scroll': {
//       to: {
//         transform: 'translateX(calc(-100% - var(--gap)))',
//       },
//     },
//     '& ul:first-child': {
//       // backgroundColor: 'yellow',
//     },
//     '& ul:last-child': {
//       // backgroundColor: 'pink',
//     },
//     '& .company, & .price': {
//       fontWeight: 'bold',
//     },
//     '& .price': {
//       marginInline: '4px',
//     },
//     // '& .plus::before': {
//     //   content: '"↑"',
//     // },
//     // '& .minus::before': {
//     //   content: '"↓"',
//     // },
//     '& .plus .change, & .plus::before': {
//       color: '#089981',
//     },
//     '& .minus .change, & .minus::before': {
//       color: '#f23645',
//     },
//     '& .plus .MuiSvgIcon-root': {
//       color: '#089981',
//     },
//     '& .minus .MuiSvgIcon-root': {
//       color: '#f23645',
//     },
//   },
// }));

// const TopMetricsWithMarquee = ({ tabs, marketStatus, }) => {
//   const router = useRouter()
//   const { data, error, isLoading, isValidating } = useSWR(["api/_getLatestPrice", TOPMETRICS_TICKETS], _getLatestPrice, { refreshInterval: 180000, keepPreviousData: true },
//     {
//       onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
//         // Never retry on 404.
//         if (error.status === 404) return

//         // Never retry for a specific key.
//         // if (key === 'api/_getLatestPrice') return

//         // Only retry up to 10 times.
//         if (retryCount >= 10) return

//         // Retry after 3 minutes.
//         setTimeout(() => revalidate({ retryCount }), 5000)
//       }
//     }
//   ) || {}

//   if (error) return null

//   const { getLatestPrice } = data || {}
//   const tickers = getLatestPrice?.reduce((acc, ticket) => {
//     if (!acc[ticket.ticker]) {
//       acc[ticket.ticker] = [];
//     }
//     acc[ticket.ticker].push(ticket);
//     return acc;
//   }, {}) || {}

//   return (
//     <Marquee>
//       <div className="stock-ticker">
//         <List sx={{ '& ul': { padding: 0 }, }}>
//           {TOPMETRICS_TICKETSANDNAMES.map(d => {
//             const t = tickers[d.t]
//             return (t && t.length > 0 ?
//               <li key={getRandomStr(7)} className={t[0]?.p >= t[1]?.p ? 'plus' : 'minus'}>
//                 <Item className="home-top-met" sx={{ cursor: d && d?.u ? 'pointer' : 'default' }} underline="none"
//                   href={d?.u ? `${d?.u}` : null}
//                   onClick={(e) => { d && d?.u ? send.uevent(e, `stock-home-top-met ${d?.t}`) : e.preventDefault(); }}>
//                   <Box component="span"
//                     sx={{
//                       boxAlign: 'center', alignItems: 'center', height: '36px', mt: .1, width: '36px', minWidth: '32px', borderRadius: '6px',
//                       marginRight: '8px', display: 'flex', bgcolor: `${t[0]?.p >= t[1]?.p ? '#e6f4ea' : '#fce8e6'}`,
//                       color: `${t[0]?.p >= t[1]?.p ? '#137333' : '#AB1C39'}`,
//                       '& svg': { height: '18px', margin: 'auto', fill: 'currentColor', flexShrink: 0 }
//                     }}
//                   >
//                     {t[0]?.p >= t[1]?.p ? <ArrowUpwardRoundedIcon /> : <ArrowDownwardRoundedIcon />}
//                   </Box>
//                   <Box component="div">
//                     <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={{ md: (0, 2), sm: (0, 2), xs: (0, 1) }} sx={{ '& a': { fontSize: '.8rem', fontWeight: 600, color: '#202124', whiteSpace: 'nowrap' } }} >
//                       <Typography component="p" className="h-tick-name" sx={{ fontSize: '.77rem', fontWeight: 600, color: '#202124', whiteSpace: 'nowrap' }}>{d.n}</Typography>
//                       {t[0]?.p && t[1]?.p ?
//                         <Typography component="p"
//                           sx={{ fontSize: '.8rem', color: isValidating ? 'rgb(154,160,166)' : `${t[0]?.p >= t[1]?.p ? 'rgb(14, 158, 25)' : '#AB1C39'}`, fontWeight: 600 }}
//                         >
//                           {getPercent(t[0]?.p, t[1]?.p)}%
//                         </Typography> : null}
//                     </Stack>
//                     <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
//                       <Typography component="p" className="h-tick-p" sx={{ fontSize: '.78rem', color: isValidating ? 'rgb(154,160,166)' : '#3c4043' }}> ${roundNum(t[0]?.p)}</Typography>
//                       {t[0]?.p && t[1]?.p ?
//                         <Typography component="p"
//                           sx={{ fontSize: '.78rem', textAlign: 'left', color: isValidating ? 'rgb(154,160,166)' : `${t[0]?.p >= t[1]?.p ? 'rgb(14, 158, 25)' : '#AB1C39'}`, fontWeight: 400 }}
//                         >
//                           {t[0]?.p - t[1]?.p >= 0 ? `+${roundNum(t[0]?.p - t[1]?.p)}` : roundNum(t[0]?.p - t[1]?.p)}
//                         </Typography> : null}
//                     </Stack>
//                   </Box>
//                 </Item>
//               </li>
//               : null)
//           })}
//         </List>
//         <List sx={{ '& ul': { padding: 0 }, }} aria-hidden="true">
//           {TOPMETRICS_TICKETSANDNAMES.map(d => {
//             const t = tickers[d.t]
//             return (t && t.length > 0 ?
//               <li key={getRandomStr(7)} className={t[0]?.p >= t[1]?.p ? 'plus' : 'minus'}>
//                 <Item className="home-top-met">
//                   <Box component="span"
//                     sx={{
//                       boxAlign: 'center', alignItems: 'center', height: '36px', mt: .1, width: '36px', minWidth: '32px', borderRadius: '6px',
//                       marginRight: '8px', display: 'flex', bgcolor: `${t[0]?.p >= t[1]?.p ? '#e6f4ea' : '#fce8e6'}`,
//                       color: `${t[0]?.p >= t[1]?.p ? '#137333' : '#AB1C39'}`,
//                       '& svg': { height: '18px', margin: 'auto', fill: 'currentColor', flexShrink: 0 }
//                     }}
//                   >
//                     {t[0]?.p >= t[1]?.p ? <ArrowUpwardRoundedIcon /> : <ArrowDownwardRoundedIcon />}
//                   </Box>
//                   <Box component="div">
//                     <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={{ md: (0, 2), sm: (0, 2), xs: (0, 1) }} sx={{ '& a': { fontSize: '.8rem', fontWeight: 600, color: '#202124', whiteSpace: 'nowrap' } }} >
//                       <Typography component="p" className="h-tick-name" sx={{ fontSize: '.77rem', fontWeight: 600, color: '#202124', whiteSpace: 'nowrap' }}>{d.n}</Typography>
//                       {t[0]?.p && t[1]?.p ?
//                         <Typography component="p"
//                           sx={{ fontSize: '.8rem', color: isValidating ? 'rgb(154,160,166)' : `${t[0]?.p >= t[1]?.p ? 'rgb(14, 158, 25)' : '#AB1C39'}`, fontWeight: 600 }}
//                         >
//                           {getPercent(t[0]?.p, t[1]?.p)}%
//                         </Typography> : null}
//                     </Stack>
//                     <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
//                       <Typography component="p" className="h-tick-p" sx={{ fontSize: '.78rem', color: isValidating ? 'rgb(154,160,166)' : '#3c4043' }}> ${roundNum(t[0]?.p)}</Typography>
//                       {t[0]?.p && t[1]?.p ?
//                         <Typography component="p"
//                           sx={{ fontSize: '.78rem', textAlign: 'left', color: isValidating ? 'rgb(154,160,166)' : `${t[0]?.p >= t[1]?.p ? 'rgb(14, 158, 25)' : '#AB1C39'}`, fontWeight: 400 }}
//                         >
//                           {t[0]?.p - t[1]?.p >= 0 ? `+${roundNum(t[0]?.p - t[1]?.p)}` : roundNum(t[0]?.p - t[1]?.p)}
//                         </Typography> : null}
//                     </Stack>
//                   </Box>
//                 </Item>
//               </li>
//               : null)
//           })}
//         </List>
//         {/* <ul>
//           {TOPMETRICS_TICKETSANDNAMES.map(d => {
//             const t = tickers[d.t]
//             return (t && t.length > 0 ?
//               <li key={getRandomStr(7)} className={t[0]?.p >= t[1]?.p ? 'plus' : 'minus'}>
//                 <span className="company">
//                   {t[0]?.p >= t[1]?.p ? <ArrowUpwardRoundedIcon sx={{ fontSize: '1.2rem', verticalAlign: 'text-bottom', color: 'inherit' }} /> : <ArrowDownwardRoundedIcon sx={{ fontSize: '1.2rem', verticalAlign: 'text-bottom', color: 'inherit' }} />}
//                   {d.t}
//                 </span>
//                 <span className="price"> ${roundNum(t[0]?.p)}</span>
//                 <span className="change">
//                   {`${t[0]?.p - t[1]?.p >= 0 ? `+${roundNum(t[0]?.p - t[1]?.p)}` : roundNum(t[0]?.p - t[1]?.p)} (${getPercent(t[0]?.p, t[1]?.p)}%)`}
//                 </span>
//               </li>
//               : null)
//           })}
//         </ul>
//         <ul aria-hidden="true">
//           {TOPMETRICS_TICKETSANDNAMES.map(d => {
//             const t = tickers[d.t]
//             return (t && t.length > 0 ?
//               <li key={getRandomStr(7)} className={t[0]?.p >= t[1]?.p ? 'plus' : 'minus'}>
//                 <span className="company">{d.t}</span>
//                 <span className="price"> ${roundNum(t[0]?.p)}</span>
//                 <span className="change">
//                   {t[0]?.p - t[1]?.p >= 0 ? `+${roundNum(t[0]?.p - t[1]?.p)}` : roundNum(t[0]?.p - t[1]?.p)}
//                   ({getPercent(t[0]?.p, t[1]?.p)}%)
//                 </span>
//               </li>
//               : null)
//           })}
//         </ul> */}
//       </div>
//       {marketStatus}
//     </Marquee>
//   )
// }

// export default TopMetricsWithMarquee
